import { useEffect, useMemo, useState } from 'react';

import { CornerDownLeft, Mic, Paperclip, Upload } from 'lucide-react';

import { toast } from '@/hooks/use-toast';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { Navbar } from '@/components/shared/navbar/navbar';

import { UploadService } from '@/lib/services/upload';
import { LeftNav } from './left-nav';

type Message = {
  type: string;
  payload: { message: string; timestamp: number };
  data: Record<string, string>;
};

const Chat = ({ messages: msgs }: { messages: Message[] }) => {
  return (
    <div className="flex-1">
      {msgs.map((msg, i) => (
        <div>
          <code key={`${msg.payload.timestamp}-${i}`}>{msg.payload.message}</code>
        </div>
      ))}
    </div>
  );
};

export const AiChat = () => {
  const [file, setFile] = useState<File | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [wsUrl, setWsUrl] = useState<string>();
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    console.log('handle submit called');
    event.preventDefault();
    if (!file) {
      toast({
        title: 'No file selected',
        description: 'Please select an audio file to upload.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    try {
      const { statusWs } = await UploadService.uploadFile(file);
      toast({
        title: 'File uploaded successfully',
        description: `${file.name} has been uploaded.`,
      });
      setFile(null);
      // Reset the file input
      const fileInput = document.getElementById('audio-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      setWsUrl(statusWs);
    } catch (error) {
      console.error('Upload failed:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your file. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    const handler = (event: { data: string }) => {
      console.log('Message from server ', JSON.parse(event.data));
      setMessages((prevMessages) => [...prevMessages, JSON.parse(event.data)]);
    };
    let ws: WebSocket;
    if (wsUrl) {
      ws = new WebSocket(wsUrl);
      ws.addEventListener('message', handler);
    }
    return () => {
      ws?.removeEventListener('message', handler);
    };
  }, [wsUrl]);

  const msgs = useMemo(() => messages, [messages]);

  return (
    <div className="grid h-screen w-full pl-[53px]">
      <LeftNav />

      <div className="flex flex-col">
        <Navbar
          links={[
            { to: '/', label: 'Dashboard' },
            { to: '/ai-chat', label: 'AI Chat' },
          ]}
        />

        <main className="grid flex-1 gap-4 overflow-auto p-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="relative hidden flex-col items-start gap-8 md:flex">
            <form className="grid w-full items-start gap-6" onSubmit={handleSubmit}>
              <fieldset className="grid gap-6 rounded-lg border p-4">
                <legend className="-ml-1 px-1 text-sm font-medium">File</legend>
                <div className="grid gap-3">
                  <Label htmlFor="model">Audio File</Label>
                  <div className="w-full items-center gap-1.5">
                    <Input
                      id="audio-file"
                      type="file"
                      accept="audio/*"
                      onChange={handleFileChange}
                      disabled={isUploading}
                    />
                  </div>
                </div>
                <Button type="submit" disabled={!file || isUploading}>
                  {isUploading ? (
                    <>
                      <Upload className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Audio
                    </>
                  )}
                </Button>
              </fieldset>
            </form>
            {/* <form className="grid w-full items-start gap-6">
              <fieldset className="grid gap-6 rounded-lg border p-4">
                <legend className="-ml-1 px-1 text-sm font-medium">Settings</legend>
                <div className="grid gap-3">
                  <Label htmlFor="model">Model</Label>
                  <Select>
                    <SelectTrigger id="model" className="items-start [&_[data-description]]:hidden">
                      <SelectValue placeholder="Select a model" />
                    </SelectTrigger>
                    <SelectContent>
                      {models.map((model) => {
                        return (
                          <SelectItem key={model.name} value={model.name}>
                            <div className="flex items-start gap-3 text-muted-foreground">
                              <Rabbit className="size-5" />
                              <div className="grid gap-0.5">
                                <p>{model.name}</p>
                                <p className="text-xs" data-description>
                                  {model.description}
                                </p>
                              </div>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-3">
                  <Label htmlFor="temperature">Temperature</Label>
                  <Input id="temperature" type="number" placeholder="0.4" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-3">
                    <Label htmlFor="top-p">Top P</Label>
                    <Input id="top-p" type="number" placeholder="0.7" />
                  </div>
                  <div className="grid gap-3">
                    <Label htmlFor="top-k">Top K</Label>
                    <Input id="top-k" type="number" placeholder="0.0" />
                  </div>
                </div>
              </fieldset>
              <fieldset className="grid gap-6 rounded-lg border p-4">
                <legend className="-ml-1 px-1 text-sm font-medium">Messages</legend>
                <div className="grid gap-3">
                  <Label htmlFor="role">Role</Label>
                  <Select defaultValue="system">
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="system">System</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="assistant">Assistant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-3">
                  <Label htmlFor="content">Content</Label>
                  <Textarea id="content" placeholder="You are a..." className="min-h-[9.5rem]" />
                </div>
              </fieldset>
            </form> */}
          </div>
          <div className="relative flex h-full min-h-[50vh] flex-col rounded-xl bg-muted/50 p-4 lg:col-span-2">
            <Badge variant="outline" className="absolute right-3 top-3">
              Output
            </Badge>
            <Chat messages={msgs} />
            <form className="relative overflow-hidden rounded-lg border bg-background focus-within:ring-1 focus-within:ring-ring">
              <Label htmlFor="message" className="sr-only">
                Message
              </Label>
              <Textarea
                id="message"
                placeholder="Type your message here..."
                className="min-h-12 resize-none border-0 p-3 shadow-none focus-visible:ring-0"
              />
              <div className="flex items-center p-3 pt-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Paperclip className="size-4" />
                      <span className="sr-only">Attach file</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Attach File</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Mic className="size-4" />
                      <span className="sr-only">Use Microphone</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Use Microphone</TooltipContent>
                </Tooltip>
                <Button type="submit" size="sm" className="ml-auto gap-1.5">
                  Send Message
                  <CornerDownLeft className="size-3.5" />
                </Button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};
